import { send } from '@actual-app/core/platform/client/connection';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';

import type { BudgetMonthCell } from '#components/reports/spreadsheets/budgetMonthCell';

export type CeldasCategoria = {
  asignado: IntegerAmount;
  /** Lo movido en el mes (negativo si es gasto). */
  movido: IntegerAmount;
  /** Lo que queda al acabar el mes (negativo si está en rojo). */
  saldo: IntegerAmount;
};

const CELDA = /^[^!]*!(budget|sum-amount|leftover)-(.+)$/;

/** Asignado, movido y saldo de cada categoría de gasto del Plan de un mes. */
export function leerCeldas(
  celdas: readonly BudgetMonthCell[],
): Map<string, CeldasCategoria> {
  const mapa = new Map<string, CeldasCategoria>();
  for (const celda of celdas) {
    const m = CELDA.exec(celda.name);
    const valor = Number(celda.value);
    if (!m || !Number.isFinite(valor)) {
      continue;
    }
    const [, tipo, id] = m;
    const actual = mapa.get(id) ?? { asignado: 0, movido: 0, saldo: 0 };
    if (tipo === 'budget') {
      actual.asignado = valor;
    } else if (tipo === 'sum-amount') {
      actual.movido = valor;
    } else {
      actual.saldo = valor;
    }
    mapa.set(id, actual);
  }
  return mapa;
}

/**
 * Las celdas del Plan del mes (`envelope-budget-month`) por categoría: lo
 * asignado, lo movido y lo que queda. Las categorías de ingreso solo traen
 * lo movido y aquí no se usan.
 */
export function useSaldosMes(mes: string): {
  saldos: Map<string, CeldasCategoria> | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['informes', 'saldos', mes],
    queryFn: async () =>
      leerCeldas(await send('envelope-budget-month', { month: mes })),
    staleTime: 30_000,
  });
  return { saldos: data ?? null, isLoading };
}
